import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { InboxesApi } from 'entities/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationKey } from 'entities/Integrations'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type InboxSettingsStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/store/inboxSettingsStore'

export class CallingGeneralStore {
  private _useOrgCallSettings = false
  private _isCallSettingsDisabled = false
  private _outboundCalls = false
  private _inboundCalls = false
  private _outboundCallsAircall = false
  private _conferenceCalling = false
  private _callTranscription = false
  private _inbox: IResponseInbox | null = null
  private _modalId = nanoid()

  constructor(private _inboxSettingsStore: InboxSettingsStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = (inbox: IResponseInbox) => {
    this._inbox = inbox
    this._useOrgCallSettings = inbox.number?.use_organization_call_settings ?? false
    this._outboundCalls = inbox.number?.voice_outbound ?? true
    this._inboundCalls = inbox.number?.voice ?? true
    this._outboundCallsAircall = inbox?.call_via_aircall?.is_active ?? false
    this._conferenceCalling = inbox.number?.conference_calling ?? false
    this._callTranscription = inbox.number?.is_transcribe_voice_to_text ?? false
    if (this._useOrgCallSettings) {
      this._isCallSettingsDisabled = true
    }
  }

  get useOrgCallSettings() {
    return this._useOrgCallSettings
  }

  get inboxId() {
    return this._inboxSettingsStore.inboxId
  }

  get outboundCalls() {
    return this._outboundCalls
  }

  get inboundCalls() {
    return this._inboundCalls
  }

  get outboundCallsAircall() {
    return this._outboundCallsAircall
  }

  get conferenceCalling() {
    return this._conferenceCalling
  }

  get callTranscription() {
    return this._callTranscription
  }

  get isCallSettingsDisabled() {
    return this._isCallSettingsDisabled
  }

  get isConferenceAndTranscriptionDisabled() {
    return !this._inboundCalls && !this._outboundCalls
  }

  get isShowOutboundCallsAircall() {
    const isAircallConnected = this._entities.integrationsStore.getIntegration(
      IntegrationKey.aircall
    )?.isConnected
    const isAircallNumber = this._inbox?.numbers.some((number) => number.is_aircall)

    return isAircallConnected || (!isAircallConnected && isAircallNumber)
  }

  toggleUseOrgCallSettings = async () => {
    this._useOrgCallSettings = !this._useOrgCallSettings
    this._isCallSettingsDisabled = this._useOrgCallSettings

    const toastMessage = this._useOrgCallSettings
      ? 'Organization settings activated'
      : 'Organization settings disabled'
    await this.updateCallSettings(toastMessage)
    await this._inboxSettingsStore.inboxLayerStore.loadTeam(this.inboxId ?? 0)
  }

  toggleOutboundCalls = () => {
    this._outboundCalls = !this._outboundCalls
    const toastMessage = this._outboundCalls
      ? 'Outbound calls activated'
      : 'Outbound calls disabled'
    this.updateCallSettings(toastMessage)
  }

  onClose = () => {
    modalStore.closeModal(this._modalId)
  }

  onHandleToggleInboundCallsAircall = async () => {
    this._inboundCalls = !this._inboundCalls
    const toastMessage = this._inboundCalls ? 'Inbound calls activated' : 'Inbound calls disabled'
    await this.updateCallSettings(toastMessage)
    await this._inboxSettingsStore.inboxLayerStore.loadTeam(this.inboxId ?? 0)
  }

  toggleInboundCalls = () => {
    if (this._inboundCalls) {
      return modalStore.addModal({
        id: this._modalId,
        type: ModalTypeList.INFO,
        title: 'Disable Inbound Calls?',
        desc: 'Disabling inbound calls will also disable the smart call flow',
        primaryAction: {
          text: 'Yes, disable',
          onAction: () => {
            this.onHandleToggleInboundCallsAircall()
            this.onClose()
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            this.onClose()
          },
        },
      })
    }

    this.onHandleToggleInboundCallsAircall()
  }

  toggleOutboundCallsAircall = () => {
    this._outboundCallsAircall = !this._outboundCallsAircall
    this.updateOutboundCallsAircall()
  }

  toggleConferenceCalling = () => {
    this._conferenceCalling = !this._conferenceCalling
    const toastMessage = this._conferenceCalling
      ? 'Conference calling activated'
      : 'Conference calling disabled'
    this.updateCallSettings(toastMessage)
  }

  onHandleToggleCallTranscription = () => {
    this._callTranscription = !this._callTranscription
    const toastMessage = this._callTranscription
      ? 'Call transcription activated'
      : 'Call transcription disabled'
    this.updateCallSettings(toastMessage)
  }

  toggleCallTranscription = () => {
    if (!this._callTranscription) {
      return modalStore.addModal({
        id: this._modalId,
        type: ModalTypeList.INFO,
        title: 'Activate call transcription?',
        desc: 'Transcription costs 1 credit per minute of transcribed audio',
        primaryAction: {
          text: 'Yes, activate',
          onAction: () => {
            this.onHandleToggleCallTranscription()
            this.onClose()
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            this.onClose()
          },
        },
      })
    }
    this.onHandleToggleCallTranscription()
  }

  updateCallSettings = async (toastMessage: string) => {
    if (!this.inboxId) return

    if (this.isConferenceAndTranscriptionDisabled) {
      this._conferenceCalling = false
      this._callTranscription = false
    }

    try {
      const { data } = await InboxesApi.updateInboxCallSettings(this.inboxId, {
        conference_calling: this._conferenceCalling,
        is_transcribe_voice_to_text: this._callTranscription,
        voice_outbound: this._outboundCalls,
        voice: this._inboundCalls,
        use_organization_call_settings: this._useOrgCallSettings,
        call_via_aircall: this._inbox?.call_via_aircall ?? null,
      })
      this._inbox = data
      this._inboundCalls = data.number?.voice ?? this._inboundCalls
      this._outboundCalls = data.number?.voice_outbound ?? this._outboundCalls
      toastStore.add({
        type: 'success',
        title: toastMessage,
      })
    } catch (error) {
      toastStore.add({
        type: 'error',
        title: 'Failed to update call settings',
      })
      logger.error(error)
    }
  }

  updateOutboundCallsAircall = async () => {
    if (!this.inboxId) return

    try {
      await InboxesApi.updateInboxOutboundCallsAircall(this.inboxId, {
        is_active: this._outboundCallsAircall,
      })
      toastStore.add({
        type: 'success',
        title: this._outboundCallsAircall
          ? 'Outbound calls via Aircall activated'
          : 'Outbound calls via Aircall disabled',
      })
    } catch (error) {
      toastStore.add({
        type: 'error',
        title: 'Failed to update outbound calls Aircall',
      })
      logger.error(error)
    }
  }
}
